// Rating tags shown in the add-comment flow, mirroring the iOS
// (`RatingTag.swift` / `RatingTagCatalog`) and Android (`RatingTag.kt`) apps.
//
// The `key` values are sent to the backend as `createComment(tagList:)` /
// `updateComment(tagList:)` and MUST stay in sync with the mobile enums — they
// are the canonical snake_case strings stored on the comment's tag list.

export interface RatingTagOption {
  key: string;
  label: string;
}

export interface RatingTagSection {
  id: 'quick' | 'conditions' | 'landscape' | 'features' | 'suitability';
  // `null` for the rating-specific "quick picks" block, which renders with no
  // header (matching the apps).
  title: string | null;
  tags: RatingTagOption[];
}

// key -> display label. Labels match the apps' English `rating_tag_*` strings.
const LABELS: Record<string, string> = {
  closed: 'Closed',
  private_property: 'Private Property',
  bad_condition: 'Bad condition',
  poor_condition: 'Poor condition',
  fair_condition: 'Fair condition',
  good_condition: 'Good condition',
  great_condition: 'Great condition',
  wrong_route: 'Wrong route',
  no_parking: 'No parking',
  has_parking: 'Has Parking',
  easy_to_park: 'Easy to park',
  hard_to_park: 'Hard to park',
  no_bathrooms: 'No bathrooms',
  has_bathroom: 'Has bathrooms',
  toilets_available: 'Toilets Available',
  tide_dependant: 'Tide dependant',
  river_crossing: 'River crossing',
  snowy: 'Snowy',
  muddy: 'Muddy',
  boggy: 'Boggy',
  rocky: 'Rocky',
  overgrown: 'Overgrown',
  crowded: 'Crowded',
  no_crowds: 'No crowds',
  scenic_views: 'Scenic views',
  great_views: 'Great views',
  dog_friendly: 'Dog-friendly',
  family_friendly: 'Family-friendly',
  kid_friendly: 'Kid-friendly',
  stroller_friendly: 'Stroller-friendly',
  wheelchair_friendly: 'Wheelchair-friendly',
  paved: 'Paved',
  partially_paved: 'Partially-paved',
  city_walk: 'City walk',
  coastal_stroll: 'Coastal stroll',
  desert_path: 'Desert path',
  forest_bathing: 'Forest bathing',
  mountain_views: 'Mountain views',
  pub_walk: 'Pub walk',
  rail_trail: 'Rail trail',
  along_the_river: 'Along the river',
  lake_views: 'Lake views',
  native_woods: 'Native woods',
  bridges_to_cross: 'Bridges to cross',
  canyon_hike: 'Canyon hike',
  caves_to_explore: 'Caves to explore',
  ancient_glaciers: 'Ancient Glaciers',
  historical_trail: 'Historical trail',
  hot_springs: 'Hot Springs',
  rock_formations: 'Rock formations',
  epic_photos: 'Epic photos',
  waterfalls_trail: 'Waterfalls trail',
  flowers_in_bloom: 'Flowers in bloom',
  wildlife_area: 'Wildlife area',
  volcanic_features: 'Volcanic Features',
  lookout_towers: 'Lookout towers',
  food: 'Food',
  coffee: 'Coffee',
};

// Rating-specific "quick picks" — the 6 tags shown immediately under the stars.
const QUICK_TAGS: Record<number, string[]> = {
  1: ['closed', 'private_property', 'bad_condition', 'wrong_route', 'no_parking', 'no_bathrooms'],
  2: ['no_parking', 'no_bathrooms', 'poor_condition', 'tide_dependant', 'boggy', 'overgrown'],
  3: ['no_bathrooms', 'crowded', 'hard_to_park', 'fair_condition', 'muddy', 'overgrown'],
  4: ['scenic_views', 'has_parking', 'good_condition', 'no_crowds', 'dog_friendly', 'has_bathroom'],
  5: ['great_views', 'easy_to_park', 'great_condition', 'no_crowds', 'dog_friendly', 'has_bathroom'],
};

// Expanded sections revealed by "More".
const LOW_STAR_CONDITIONS = [
  'tide_dependant', 'river_crossing', 'snowy', 'muddy', 'boggy', 'rocky', 'overgrown',
  'no_parking', 'no_bathrooms', 'closed', 'private_property', 'wrong_route', 'bad_condition', 'hard_to_park',
];

const HIGH_STAR_LANDSCAPE = [
  'city_walk', 'coastal_stroll', 'desert_path', 'forest_bathing', 'mountain_views',
  'pub_walk', 'rail_trail', 'along_the_river', 'lake_views', 'native_woods',
];

const HIGH_STAR_FEATURES = [
  'bridges_to_cross', 'canyon_hike', 'caves_to_explore', 'ancient_glaciers', 'historical_trail',
  'hot_springs', 'rock_formations', 'epic_photos', 'waterfalls_trail', 'flowers_in_bloom',
  'wildlife_area', 'volcanic_features', 'lookout_towers', 'scenic_views',
];

const HIGH_STAR_SUITABILITY = [
  'dog_friendly', 'family_friendly', 'kid_friendly', 'stroller_friendly', 'wheelchair_friendly',
  'paved', 'partially_paved', 'toilets_available',
];

const HIGH_STAR_CONDITIONS = [
  'has_parking', 'food', 'coffee', 'has_bathroom', 'fair_condition', 'good_condition', 'great_condition',
];

export const ratingTagLabel = (key: string): string =>
  LABELS[key] || key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const toOptions = (keys: string[]): RatingTagOption[] =>
  keys.map((key) => ({ key, label: ratingTagLabel(key) }));

// Ordered sections shown on the rating-tags step for the given star value.
// Sections render top-to-bottom; the first is always the rating-specific
// "quick picks" with no header. Mirrors `RatingTagCatalog.sections(for:)`.
export const ratingTagSections = (rating: number): RatingTagSection[] => {
  if (!Number.isInteger(rating) || rating < 1 || rating > 5) return [];

  const quick: RatingTagSection = { id: 'quick', title: null, tags: toOptions(QUICK_TAGS[rating]) };

  if (rating <= 3) {
    return [
      quick,
      { id: 'conditions', title: 'Conditions', tags: toOptions(LOW_STAR_CONDITIONS) },
    ];
  }

  return [
    quick,
    { id: 'landscape', title: 'Landscape', tags: toOptions(HIGH_STAR_LANDSCAPE) },
    { id: 'features', title: 'Features', tags: toOptions(HIGH_STAR_FEATURES) },
    { id: 'suitability', title: 'Suitability', tags: toOptions(HIGH_STAR_SUITABILITY) },
    { id: 'conditions', title: 'Conditions', tags: toOptions(HIGH_STAR_CONDITIONS) },
  ];
};
